// Post Composer Component
import React, { useState, useEffect } from 'react';
import { Calendar, Image, Hash, Send, Sparkles } from 'lucide-react';
import { SocialPlatform, SocialPost } from '../types/social.types';
import { useSocialPosts } from '../hooks/useSocialPosts';
import AIPostGenerator from './AIPostGenerator';
import PostPreview from './PostPreview';
import PublishScheduler from './PublishScheduler';
import { mediaUploadService } from '../services/mediaUploadService';

const PostComposer: React.FC = () => {
  const { createPost, updatePost, publishPost } = useSocialPosts();
  const [activeTab, setActiveTab] = useState<'compose' | 'ai' | 'schedule'>('compose');
  const [currentPost, setCurrentPost] = useState<Partial<SocialPost>>({
    content: '',
    images: [],
    platforms: [],
    status: 'draft',
    ai_generated: false
  });
  const [selectedPlatforms, setSelectedPlatforms] = useState<SocialPlatform[]>([]);
  const [isPublishing, setIsPublishing] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [connectedPlatforms, setConnectedPlatforms] = useState<Record<string, boolean>>({});
  const [uploadedMedia, setUploadedMedia] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  const platforms: { id: SocialPlatform; name: string; icon: string; maxLength: number }[] = [
    { id: 'facebook', name: 'Facebook', icon: '📘', maxLength: 63206 },
    { id: 'linkedin', name: 'LinkedIn', icon: '💼', maxLength: 3000 },
    { id: 'instagram', name: 'Instagram', icon: '📸', maxLength: 2200 },
    { id: 'twitter', name: 'Twitter/X', icon: '🐦', maxLength: 280 }
  ];

  // Sprawdź połączone platformy przy montowaniu
  useEffect(() => {
    checkConnectedPlatforms();
    
    // Odśwież status gdy okno otrzyma focus
    const handleFocus = () => checkConnectedPlatforms();
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  const checkConnectedPlatforms = () => {
    try {
      const storedTokens = JSON.parse(localStorage.getItem('social_tokens') || '{}');
      const connected: Record<string, boolean> = {};
      
      connected['facebook'] = !!(storedTokens.facebook?.token);
      connected['linkedin'] = !!(storedTokens.linkedin?.token);
      connected['instagram'] = !!(storedTokens.instagram?.token);
      connected['twitter'] = !!(storedTokens.twitter?.token);
      
      setConnectedPlatforms(connected);
    } catch (error) {
      console.error('Error checking connected platforms:', error);
    }
  };

  const handlePlatformToggle = (platform: SocialPlatform) => {
    setSelectedPlatforms(prev =>
      prev.includes(platform)
        ? prev.filter(p => p !== platform)
        : [...prev, platform]
    );
  };

  const handleContentChange = (content: string) => {
    setCurrentPost(prev => ({ ...prev, content }));
  };

  const handleAIGenerated = (content: string, platforms: SocialPlatform[]) => {
    setCurrentPost(prev => ({
      ...prev,
      content,
      ai_generated: true,
      platforms
    }));
    setSelectedPlatforms(platforms);
    setActiveTab('compose');
  };

  const handleSaveDraft = async () => {
    try {
      const postData = {
        ...currentPost,
        platforms: selectedPlatforms,
        status: 'draft' as const
      };
      
      if (currentPost.id) {
        await updatePost(currentPost.id, postData);
      } else {
        const newPost = await createPost(postData as Omit<SocialPost, 'id' | 'created_at'>);
        setCurrentPost(newPost);
      }
      
      alert('Szkic zapisany!');
    } catch (error) {
      console.error('Error saving draft:', error);
      alert('Błąd podczas zapisywania szkicu');
    }
  };

  const handlePublishNow = async () => {
    if (!currentPost.content || selectedPlatforms.length === 0) {
      alert('Uzupełnij treść i wybierz platformy');
      return;
    }

    setIsPublishing(true);
    try {
      let postId = currentPost.id;
      
      if (!postId) {
        const newPost = await createPost({
          ...currentPost,
          platforms: selectedPlatforms,
          status: 'draft'
        } as Omit<SocialPost, 'id' | 'created_at'>);
        postId = newPost.id;
      }

      await publishPost({
        post_id: postId,
        platforms: selectedPlatforms
      });

      alert('Post opublikowany!');
      // Reset form
      setCurrentPost({
        content: '',
        images: [],
        platforms: [],
        status: 'draft',
        ai_generated: false
      });
      setSelectedPlatforms([]);
    } catch (error) {
      console.error('Error publishing post:', error);
      alert('Błąd podczas publikowania');
    } finally {
      setIsPublishing(false);
    }
  };

  const extractHashtags = (content: string): string[] => {
    const regex = /#\w+/g;
    const matches = content.match(regex);
    return matches ? matches.map(tag => tag.substring(1)) : [];
  };

  const contentLength = currentPost.content?.length || 0;
  const hashtags = extractHashtags(currentPost.content || '');

  return (
    <div className="bg-zinc-800 rounded-lg shadow-xl border border-zinc-700">
      <div className="border-b border-zinc-700">
        <div className="flex">
          <button
            onClick={() => setActiveTab('compose')}
            className={`px-6 py-3 font-medium transition-all ${
              activeTab === 'compose'
                ? 'text-blue-500 border-b-2 border-blue-500'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            ✍️ Komponuj
          </button>
          <button
            onClick={() => setActiveTab('ai')}
            className={`px-6 py-3 font-medium transition-all ${
              activeTab === 'ai'
                ? 'text-blue-500 border-b-2 border-blue-500'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <Sparkles className="inline w-4 h-4 mr-1" />
            Generator AI
          </button>
          <button
            onClick={() => setActiveTab('schedule')}
            className={`px-6 py-3 font-medium transition-all ${
              activeTab === 'schedule'
                ? 'text-blue-500 border-b-2 border-blue-500'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <Calendar className="inline w-4 h-4 mr-1" />
            Harmonogram
          </button>
        </div>
      </div>

      <div className="p-6">
        {activeTab === 'compose' && (
          <div className="space-y-6">
            {/* Wybór platform */}
            <div>
              <h3 className="text-sm font-medium text-gray-300 mb-3">
                Wybierz platformy
              </h3>
              <div className="flex flex-wrap gap-3">
                {platforms.map(platform => (
                  <div key={platform.id} className="relative">
                    <button
                      onClick={() => handlePlatformToggle(platform.id)}
                      disabled={!connectedPlatforms[platform.id]}
                      className={`px-4 py-2 rounded-lg border-2 transition-all ${
                        selectedPlatforms.includes(platform.id)
                          ? 'border-blue-500 bg-blue-600/20 text-blue-400'
                          : connectedPlatforms[platform.id]
                            ? 'border-zinc-600 hover:border-zinc-500 text-gray-400 hover:text-white'
                            : 'border-zinc-700 bg-zinc-900 text-gray-600 cursor-not-allowed'
                      }`}
                    >
                      <span className="mr-2">{platform.icon}</span>
                      {platform.name}
                      {!connectedPlatforms[platform.id] && (
                        <span className="ml-2 text-xs text-yellow-400">🔒</span>
                      )}
                    </button>
                    {!connectedPlatforms[platform.id] && (
                      <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-zinc-900 text-xs text-yellow-400 px-2 py-1 rounded opacity-0 hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">
                        Połącz konto w ustawieniach
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Edytor treści */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="text-sm font-medium text-gray-300">
                  Treść posta
                </label>
                <div className="flex items-center gap-4 text-sm">
                  {currentPost.ai_generated && (
                    <span className="text-purple-400">
                      <Sparkles className="inline w-4 h-4 mr-1" />
                      Wygenerowane przez AI
                    </span>
                  )}
                  <span className={`${
                    contentLength > 280 ? 'text-orange-400' : 'text-gray-400'
                  }`}>
                    {contentLength} znaków
                  </span>
                </div>
              </div>
              <textarea
                value={currentPost.content || ''}
                onChange={(e) => handleContentChange(e.target.value)}
                placeholder="Co chcesz powiedzieć swoim obserwatorom?"
                className="w-full p-3 bg-zinc-900 border border-zinc-700 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white placeholder-gray-500"
                rows={6}
              />
              
              {/* Ostrzeżenia o długości */}
              {selectedPlatforms.includes('twitter') && contentLength > 280 && (
                <p className="text-sm text-orange-400 mt-1">
                  ⚠️ Post jest za długi dla Twittera (max 280 znaków)
                </p>
              )}
            </div>

            {/* Hashtagi */}
            {hashtags.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-300 mb-2">
                  <Hash className="inline w-4 h-4 mr-1" />
                  Wykryte hashtagi
                </h4>
                <div className="flex flex-wrap gap-2">
                  {hashtags.map((tag, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-blue-600/20 text-blue-400 rounded-full text-sm"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Dodawanie obrazów */}
            <div>
              <h4 className="text-sm font-medium text-gray-300 mb-2">
                <Image className="inline w-4 h-4 mr-1" />
                Multimedia
              </h4>
              <div className="border-2 border-dashed border-zinc-600 rounded-lg p-8 text-center hover:border-zinc-500 transition-colors">
                <input
                  type="file"
                  multiple
                  accept="image/*,video/*"
                  className="hidden"
                  id="media-upload"
                  onChange={async (e) => {
                    const files = e.target.files;
                    if (!files || files.length === 0) return;
                    
                    setIsUploading(true);
                    try {
                      const uploadPromises = Array.from(files).map(async (file) => {
                        // Optymalizuj obraz jeśli to obraz
                        const fileToUpload = file.type.startsWith('image/') 
                          ? await mediaUploadService.optimizeImage(file)
                          : file;
                        
                        const result = await mediaUploadService.upload(fileToUpload);
                        return result.url;
                      });
                      
                      const urls = await Promise.all(uploadPromises);
                      setUploadedMedia(prev => [...prev, ...urls]);
                      setCurrentPost(prev => ({
                        ...prev,
                        images: [...(prev.images || []), ...urls]
                      }));
                      
                      alert(`✅ Pomyślnie wgrano ${urls.length} plików`);
                    } catch (error) {
                      console.error('Upload error:', error);
                      alert(`❌ Błąd podczas wgrywania: ${error instanceof Error ? error.message : 'Nieznany błąd'}`);
                    } finally {
                      setIsUploading(false);
                      // Reset input
                      e.target.value = '';
                    }
                  }}
                />
                <label
                  htmlFor="media-upload"
                  className="cursor-pointer text-gray-400 hover:text-white"
                >
                  {isUploading ? (
                    <div className="flex flex-col items-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-2"></div>
                      <p>Wgrywanie...</p>
                    </div>
                  ) : (
                    <>
                      <Image className="w-12 h-12 mx-auto mb-2 text-gray-600" />
                      <p>Kliknij lub przeciągnij pliki</p>
                      <p className="text-sm text-gray-500 mt-1">
                        JPG, PNG, GIF, MP4 (max 10MB)
                      </p>
                    </>
                  )}
                </label>
              </div>
              
              {/* Podgląd wgranych mediów */}
              {uploadedMedia.length > 0 && (
                <div className="mt-4">
                  <p className="text-sm text-gray-400 mb-2">Wgrane media ({uploadedMedia.length}):</p>
                  <div className="grid grid-cols-4 gap-2">
                    {uploadedMedia.map((url, index) => (
                      <div key={index} className="relative group">
                        <img 
                          src={url} 
                          alt={`Media ${index + 1}`}
                          className="w-full h-24 object-cover rounded-lg border border-zinc-600"
                        />
                        <button
                          onClick={() => {
                            setUploadedMedia(prev => prev.filter((_, i) => i !== index));
                            setCurrentPost(prev => ({
                              ...prev,
                              images: prev.images?.filter((_, i) => i !== index) || []
                            }));
                          }}
                          className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          ❌
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Przyciski akcji */}
            <div className="flex justify-between items-center pt-4 border-t border-zinc-700">
              <div className="flex gap-2">
                <button
                  onClick={() => setShowPreview(!showPreview)}
                  className="px-4 py-2 text-gray-300 hover:bg-zinc-700 rounded-lg transition-all"
                >
                  👁️ Podgląd
                </button>
                <button
                  onClick={handleSaveDraft}
                  className="px-4 py-2 text-gray-300 hover:bg-zinc-700 rounded-lg transition-all"
                >
                  💾 Zapisz szkic
                </button>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setActiveTab('schedule')}
                  className="px-4 py-2 bg-zinc-700 text-gray-300 rounded-lg hover:bg-zinc-600 transition-all"
                >
                  <Calendar className="inline w-4 h-4 mr-2" />
                  Zaplanuj
                </button>
                <button
                  onClick={handlePublishNow}
                  disabled={!currentPost.content || selectedPlatforms.length === 0 || isPublishing}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  <Send className="inline w-4 h-4 mr-2" />
                  {isPublishing ? 'Publikowanie...' : 'Opublikuj teraz'}
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'ai' && (
          <AIPostGenerator onGenerate={handleAIGenerated} />
        )}

        {activeTab === 'schedule' && currentPost.content && (
          <PublishScheduler
            post={currentPost as SocialPost}
            platforms={selectedPlatforms}
            onSchedule={(date) => {
              console.log('Scheduled for:', date);
              setActiveTab('compose');
            }}
          />
        )}
      </div>

      {/* Podgląd */}
      {showPreview && currentPost.content && (
        <PostPreview
          content={currentPost.content}
          platforms={selectedPlatforms}
          onClose={() => setShowPreview(false)}
        />
      )}
    </div>
  );
};

export default PostComposer;
